import React from 'react';
import { useTable, useSortBy } from 'react-table';
import PropTypes from 'prop-types';
import styles from '../styles/Table.module.scss';
import iconArrowDown from '../assets/images/icon-arrow-down.svg';
import useGlossary from '../hooks/useGlossary';
import PDFLink from './PDFLink';

function CountryResultsTable({ data, region, limit }) {
  const g = useGlossary;

  const headerToGlossary = {
    Rank: 'rank',
    'Gains/ Losses': 'gainsLosses',
    Country: 'country',
    Breadth: 'breadth',
    Depth: 'depth',
    'Country Profile': 'countryProfile',
    Score: 'score',
  };

  const columns = React.useMemo(
    () => [
      { Header: 'Rank', accessor: 'rank' },
      {
        Header: 'Gains/ Losses',
        accessor: 'change',
        sortInverted: true,
        sortType: 'basic',
      },
      { Header: 'Country', accessor: 'country' },
      {
        Header: 'Score',
        accessor: 'score',
        sortType: 'basic',
        sortInverted: true,
      },
      {
        Header: 'Depth',
        accessor: 'depth_score',
        sortType: 'basic',
        sortInverted: true,
      },
      {
        Header: 'Breadth',
        accessor: 'breadth_score',
        sortType: 'basic',
        sortInverted: true,
      },
      {
        Header: 'Country Profile',
        accessor: 'pdf_download',
        disableSortBy: true,
      },
    ],
    [],
  );

  const initialState = {
    sortBy: [
      {
        id: 'rank',
        desc: false,
      },
    ],
  };

  const { rows, prepareRow, headers } = useTable(
    {
      columns,
      data,
      initialState,
      autoResetSortBy: false,
    },
    useSortBy,
  );

  const allFiles = ((ctx) => {
    const keys = ctx.keys();
    const values = keys.map(ctx);
    return keys.reduce((o, k, i) => {
      const ob = o;
      ob[k] = values[i];
      return ob;
    }, {});
  })(require.context('../assets/countries/flags/', true, /.*/));

  const flagOverrides = {
    'Syrian Arab Republic': 'syria',
    Türkiye: 'tuerkiye-turkey',
  };

  const getFlag = (country) => {
    let link;

    const c = flagOverrides[country] || country.toLowerCase()
      .replace(/[()]/g, '')
      .replace(/ü/g, 'ue')
      .replace(/ô/, 'o')
      .replace(/ã/g, 'a')
      .replace(/é/g, 'e')
      .replace(/í/g, 'i')
      .replace(/\W/g, '-')
      .replace(/-+/g, '-')
      .replace(/^st-/, 'saint-');

    try {
      link = allFiles[`./${c}.svg`];
    } catch (error) {
      // console.log(error);
    }

    if (link) {
      return <img src={link} alt={country} className={styles.countryFlag} />;
    }
    return <span className={styles.emptyFlag} />;
  };

  const renderCell = (cell) => {
    const sizeFactor = 80;
    if (cell.column.id === 'rank' && region !== 'global') {
      return <span>{cell.row.index + 1}</span>;
    }
    if (cell.column.id === 'breadth_score') {
      return (
        <span
          className={styles.breadthCell}
          style={{
            width: `${(cell.row.values.breadth_score / cell.row.values.score) * sizeFactor}px`,
          }}
        >
          <span className={styles.popoverScore}>
            {cell.row.values.breadth_score.toFixed(0)}
          </span>
        </span>
      );
    }
    if (cell.column.id === 'score') {
      return (
        <span>
          {cell.value.toFixed(1)}
        </span>
      );
    }
    if (cell.column.id === 'depth_score') {
      return (
        <span
          className={styles.depthCell}
          style={{
            width: `${(cell.row.values.depth_score / cell.row.values.score) * sizeFactor}px`,
          }}
        >
          <span className={styles.popoverScore}>
            {cell.row.values.depth_score.toFixed(0)}
          </span>
        </span>
      );
    }
    if (cell.column.id === 'change') {
      return (
        // prettier-ignore
        <span className={`${cell.value > 0 && styles.changePositive} ${cell.value < 0 && styles.changeNegative}`}>
          {cell.value !== 0 ? cell.value : ''}
        </span>
      );
    }
    if (cell.column.id === 'country') {
      return (
        <span>
          {getFlag(cell.value)}
          {cell.value}
        </span>
      );
    }
    if (cell.column.id === 'pdf_download') {
      return (
        <span className={styles.pdfDownloadCell}>
          <PDFLink iso={cell.row.original.iso} title={cell.row.original.country} />
        </span>
      );
    }
    return cell.value;
  };

  const limitedData = rows.slice(0, limit);

  return (
    <table className={styles.general}>
      <thead>
        <tr>
          {headers.map((header) => (
            <th
              /* eslint-disable react/jsx-props-no-spreading */
              {...header.getHeaderProps(header.getSortByToggleProps())}
              /* eslint-enable react/jsx-props-no-spreading */
              className={styles[header.id]}
            >
              {g({ key: headerToGlossary[header.render('Header')] })}
              <span>
                {(() => {
                  if (header.isSorted) {
                    if (header.isSortedDesc) {
                      return (
                        <img
                          src={iconArrowDown}
                          style={{
                            marginLeft: '.3em',
                            transform: 'rotate(180deg) translateY(.1em)',
                          }}
                          alt="sort-des"
                        />
                      );
                    }
                    return (
                      <img
                        src={iconArrowDown}
                        style={{
                          marginLeft: '.3em',
                          transform: 'translateY(-.1em)',
                        }}
                        alt="sort-asc"
                      />
                    );
                  }
                  return (
                    <img
                      src={iconArrowDown}
                      style={{
                        marginLeft: '.3em',
                        opacity: '0',
                      }}
                      alt="placeholder"
                    />
                  );
                })()}
              </span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {limitedData.map((row) => {
          prepareRow(row);

          return (
            /* eslint-disable react/jsx-props-no-spreading */
            <tr {...row.getRowProps()}>
              {/* eslint-enable react/jsx-props-no-spreading */}
              {row.cells.map((cell) => (
                /* eslint-disable react/jsx-props-no-spreading */
                <td
                  {...cell.getCellProps()}
                  className={styles[cell.column.id]}
                >
                  {/* eslint-enable react/jsx-props-no-spreading */}
                  {renderCell(cell)}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export default CountryResultsTable;

CountryResultsTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      advem: PropTypes.string,
      breadth_score: PropTypes.number,
      change: PropTypes.number,
      country: PropTypes.string,
      depth_score: PropTypes.number,
      euasean: PropTypes.string,
      iso: PropTypes.string,
      oecd: PropTypes.number,
      rank: PropTypes.number,
      region: PropTypes.string,
    }),
  ).isRequired,
  region: PropTypes.string.isRequired,
  limit: PropTypes.number.isRequired,
};
